'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LogoSvg } from '@/components/svg/logo';
import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { subscribeNewsletter } from '@/actions/contact/subscribe-newsletter';
import { withLocaleHref, type Locale } from '@/i18n/config';

export function LandingFooter() {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const pathname = usePathname();
  const locale = (pathname.split('/')[1] || 'en') as Locale;
  const t = useTranslations('LandingPage');
  const isHome = pathname === `/${locale}` || pathname === '/';
  const baseHref = isHome ? '' : `/${locale}`;

  async function handleSubscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribeStatus('loading');
    const result = await subscribeNewsletter(email.trim());
    if (result.success) {
      setSubscribeStatus('success');
      setEmail('');
    } else {
      setSubscribeStatus('error');
    }
  }

  return (
    <footer className="w-full border-t bg-muted/30">
      <div className="w-full px-5 py-12 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="space-y-4">
            <Link
              href={withLocaleHref('/', locale)}
              className="inline-flex items-center gap-2"
            >
              <LogoSvg width="140" height="28" />
            </Link>
            <p className="text-sm text-muted-foreground max-w-xs">
              {t('footerTagline')}
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('footerProduct')}</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  href={`${baseHref}#features-header`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerFeatures')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${baseHref}#benefits`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerPricing')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${baseHref}#how-it-works`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerHowItWorks')}
                </Link>
              </li>
              <li>
                <Link
                  href={`${baseHref}#faq`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerFaq')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('footerCompany')}</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  href={withLocaleHref('/about', locale)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerAbout')}
                </Link>
              </li>
              <li>
                <Link
                  href={withLocaleHref('/contact', locale)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerContact')}
                </Link>
              </li>
              <li>
                <Link
                  href={withLocaleHref('/login', locale)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerLogin')}
                </Link>
              </li>
              <li>
                <Link
                  href={withLocaleHref('/register', locale)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t('footerRegister')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('footerSubscribe')}</h4>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                placeholder={t('footerEmailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={subscribeStatus === 'loading'}
                className="flex-1 min-w-0"
              />
              <Button
                type="submit"
                size="default"
                disabled={subscribeStatus === 'loading'}
              >
                {subscribeStatus === 'loading'
                  ? t('footerSubscribing')
                  : t('footerJoin')}
              </Button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                {t('footerSubscribeSuccess')}
              </p>
            )}
            {subscribeStatus === 'error' && (
              <p className="mt-2 text-sm text-destructive">
                {t('footerSubscribeError')}
              </p>
            )}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
          <span>
            {t('footerCopyright', { year: new Date().getFullYear() })}
          </span>
          <div className="flex gap-6">
            <Link
              href={withLocaleHref('/privacy', locale)}
              className="hover:text-foreground transition-colors"
            >
              {t('footerPrivacy')}
            </Link>
            <Link
              href={withLocaleHref('/terms', locale)}
              className="hover:text-foreground transition-colors"
            >
              {t('footerTerms')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
